// The `removal_preview` block: what deleting a split would cost, before it is deleted.
//
// Every number here is computed by rgip and merged over the model's block server-side
// (`app/runs/authored.py`), so the model writes `headline` and nothing else. That split
// is the whole point of the card. The agent used to narrate the counts in prose, which
// put the destroyed-node count in the hands of the thing least able to carry it: on a
// tree review the same model dropped whole fields from every finding against a prompt
// that told it to copy the object byte for byte.
//
// So this parser takes the numbers and treats the prose as optional. A block missing its
// headline still renders; a block missing its counts is not a preview and returns null,
// falling through to the markdown fallback the way every other block here does.

import { Choices, parseChoices } from './choicesBlock';

export interface RemovalPreview {
  /** The model's one line of context. May be empty. */
  headline: string;
  tree: number | null;
  node: number | null;
  /** Nodes destroyed BESIDES the one named, which survives as a leaf. */
  wouldDestroyNodes: number;
  /** Leads that land back in this group as one undivided leaf. */
  populationReturning: number | null;
  /**
   * The node's own condition, one entry per ANDed test.
   *
   * More than one is the case worth drawing loudly. A clean root reads as a single cut
   * and is four lead-quality flags stacked together, so "remove the exclusions split"
   * and "remove the cleaning you depend on" are the same write, and the parts list is
   * the only thing on screen that says so.
   */
  conditionParts: string[];
  isCompound: boolean;
  choices: Choices | null;
}

function asInt(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value)
    ? Math.trunc(value)
    : null;
}

/** Read a removal preview out of a block body, or null when it is not one. */
export function parseRemovalPreview(raw: string): RemovalPreview | null {
  let doc: unknown;
  try {
    doc = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof doc !== 'object' || doc === null) return null;
  const record = doc as Record<string, unknown>;

  // The one field that makes this a removal preview rather than some other block. Zero
  // is a real answer (a split whose branches are both leaves destroys nothing beyond
  // them), so this checks the type and not the truthiness.
  const wouldDestroyNodes = asInt(record.would_destroy_nodes);
  if (wouldDestroyNodes === null) return null;

  const parts = Array.isArray(record.condition_parts)
    ? record.condition_parts.filter(
        (p): p is string => typeof p === 'string' && !!p.trim()
      )
    : [];

  return {
    headline: typeof record.headline === 'string' ? record.headline.trim() : '',
    tree: asInt(record.tree),
    node: asInt(record.node),
    wouldDestroyNodes,
    populationReturning: asInt(record.population_returning),
    conditionParts: parts,
    // Derived from the parts actually present rather than read off the flag, so a card
    // can never say "4 conditions in one" above a list showing one.
    isCompound: parts.length > 1,
    choices:
      typeof record.choices === 'object' && record.choices !== null
        ? parseChoices(JSON.stringify(record.choices))
        : null,
  };
}
